import React, { useState } from 'react';
import { Avatar, Box, Button, Typography } from '@mui/material';
import { Circle as CircleIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { apiErrorMessage } from 'shared/helpers/apiError';
import { useSnackbar } from 'shared/contexts/SnackbarContext';
import { ACCEPT_BUTTON, DECLINE_BUTTON, answerRequest } from 'components/social/connections/connectionsApi';

/** Someone asked to connect with you (Accept / Decline here), or accepted your request (interact/follow_request/viewsets.py). */
export default function ConnectionNotificationCard({ notification, onMarkRead }) {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [busy, setBusy] = useState(false);
  const p = notification.payload || {};
  const who = p.from_user || {};
  const asking = notification.ntype === 'follow_request';
  const open = () => {
    if (!notification.is_read && onMarkRead) onMarkRead();
    if (who.id) navigate(`/profile/${who.id}/`);
  };
  const answer = async (e, verb) => {
    e.stopPropagation();
    setBusy(true);
    try {
      await answerRequest(p.request_id, verb);
      showSnackbar(verb === 'accept' ? `You are now connected with ${(who.name || '').split(' ')[0]}` : 'Request declined', 'success');
    } catch (err) {
      showSnackbar(apiErrorMessage(err, 'This request is no longer waiting for an answer.'), 'error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box onClick={open} sx={{
      display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1.25, px: 2, borderBottom: '1px solid', borderColor: 'divider',
      backgroundColor: notification.is_read ? 'transparent' : 'action.hover', cursor: 'pointer', '&:hover': { backgroundColor: 'action.selected' },
    }}>
      <Avatar src={who.avatar_url || undefined} sx={{ width: 34, height: 34, mt: 0.25, bgcolor: 'primary.avatar', color: '#fff' }}>
        {(who.name || '?')[0]}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2">
          <Box component="span" fontWeight={600}>{who.name || 'Someone'}</Box>
          <Box component="span" color="text.secondary">{asking ? ' wants to connect with you' : ' accepted your connection request'}</Box>
        </Typography>
        {asking && p.message && (
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block' }}>“{p.message}”</Typography>
        )}
        {asking && (
          <Box sx={{ display: 'flex', gap: 1, mt: 0.75 }}>
            <Button {...ACCEPT_BUTTON} disabled={busy} onClick={(e) => answer(e, 'accept')}>Accept</Button>
            <Button {...DECLINE_BUTTON} disabled={busy} onClick={(e) => answer(e, 'decline')}>Decline</Button>
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {formatDistanceToNow(new Date(notification.created), { addSuffix: true })}
        </Typography>
        {!notification.is_read && <CircleIcon sx={{ fontSize: 8, color: 'primary.main' }} />}
      </Box>
    </Box>
  );
}
